import { defineStore } from 'pinia';
import {
  SET_QUICK_VIEW_PRODUCT_DETAILS,
  SET_REGISTRANT_ITEM_API_RESPONSE,
  SET_REGISTRANT_REQUESTED_QTY,
  SET_REGISTRANT_PURCHASED_QTY,
  SET_ITEM_QTY,
  SET_UPC_DETAILS,
  SET_UPDATED_SIZE,
  SET_UPDATED_COLOR,
  SET_STORE_ERROR_STATE,
  SET_REGISTRY_ITEM_STATUS,
  UPDATE_PREFERENCE_STATUS,
  SET_QUICK_VIEW_SAVE_ERROR_STATE,
  SET_QUICK_VIEW_PRODUCT_DETAILS_META,
  SET_PRODUCT_DETAILS_API_ERROR_RESPONSE,
  UPDATE_GIFTS_PURCHASED,
  SET_GIFT_CARD_AMOUNT,
  SET_GIFT_CARD_MESSAGE,
  SET_IN_VALID_GIFT_CARD_AMOUNT,
  SET_GIFT_CARD_DELIVERY_OPTION,
  SET_ATB_COMPONENT,
  SET_ATB_LOADING_STATE,
} from '~/types/mutations';

import { FETCH_QUICK_VIEW_PRODUCT_DETAILS, UPDATE_PREFERENCE, ADD_PURCHASE } from '~/types/actions';

import { SoftSignInValidation } from '~/utils/SoftSignInValidation.js';
import { httpUpdateAPIHeaders } from '~/utils/APIUtil';
import { generateLogin365FingerPrint } from '~/utils/PersistentLoginUtil';
import { useEnvPropsStore } from '~/stores/envProps';
import { useHeaderBannerStore } from '~/stores/headerBanner';
import $http from '@vue-util/http';

const UPDATE_PREFERENCE_ENDPOINT = '/regsvc/registry/api/v1/preference/updatePreference';
const ADD_PURCHASE_ENDPOINT = '/regsvc/registry/api/v1/purchase/addPurchase';

export const useQuickViewStore = defineStore('quickView', {
  state: () => ({
    quickViewProductDetails: null,
    quickViewProductDetailsMeta: null,
    quickViewSaveErrorState: false,
    registrantItemQuantitiesApiResponse: null,
    productDetailsApiErrorResponse: null,
    registrantRequestedQty: null,
    registrantPurchasedQty: 0,
    itemQty: 1,
    upcDetails: {},
    errorState: false,
    registryItemStatus: {},
    updatePreferenceStatus: true,
    giftCardAmount: null,
    giftCardMessage: '',
    inValidGiftCardAmount: true,
    giftCardDeliveryOption: '',
    ATBComponent: null,
    gridLoadingProductId: null,
    quickViewLoadingProductId: null,
    atbSource: null,
  }),
  actions: {
    [SET_ATB_LOADING_STATE](payload) {
      const { source, productId } = payload;
      this.atbSource = source;
      if (source === 'GRID') {
        this.gridLoadingProductId = productId;
      } else if (source === 'QUICKVIEW') {
        this.quickViewLoadingProductId = productId;
      }
    },
    [SET_QUICK_VIEW_PRODUCT_DETAILS](data) {
      this.quickViewProductDetails = data;
    },
    [SET_ATB_COMPONENT](data) {
      this.ATBComponent = data;
      if (data === null) {
        this.atbSource = null;
      }
    },
    [SET_QUICK_VIEW_PRODUCT_DETAILS_META](data) {
      this.quickViewProductDetailsMeta = data;
    },
    [SET_REGISTRANT_ITEM_API_RESPONSE](data) {
      this.registrantItemQuantitiesApiResponse = data;
    },
    [SET_REGISTRANT_REQUESTED_QTY](data) {
      this.registrantRequestedQty = data;
    },
    [SET_REGISTRANT_PURCHASED_QTY](data) {
      this.registrantPurchasedQty = data;
    },
    [SET_ITEM_QTY](data) {
      this.itemQty = data;
    },
    [SET_UPC_DETAILS](data) {
      this.upcDetails = data;
    },
    [SET_UPDATED_SIZE](data) {
      this.selectedSize = data.value;
    },
    [SET_UPDATED_COLOR](data) {
      this.selectedColor = data;
    },
    [SET_REGISTRY_ITEM_STATUS](data) {
      this.registryItemStatus = data;
    },
    [UPDATE_PREFERENCE_STATUS](data) {
      this.updatePreferenceStatus = data;
    },
    [SET_STORE_ERROR_STATE](payload) {
      this.errorState = payload;
    },
    [SET_QUICK_VIEW_SAVE_ERROR_STATE](error) {
      this.quickViewSaveErrorState = error;
    },
    [SET_PRODUCT_DETAILS_API_ERROR_RESPONSE](error) {
      this.productDetailsApiErrorResponse = error;
    },
    [SET_GIFT_CARD_AMOUNT](data) {
      this.giftCardAmount = data;
    },
    [SET_GIFT_CARD_MESSAGE](data) {
      this.giftCardMessage = data;
    },
    [SET_IN_VALID_GIFT_CARD_AMOUNT](data) {
      this.inValidGiftCardAmount = data;
    },
    [SET_GIFT_CARD_DELIVERY_OPTION](data) {
      this.giftCardDeliveryOption = data;
    },
    async [FETCH_QUICK_VIEW_PRODUCT_DETAILS](params) {
      const envPropsStore = useEnvPropsStore();
      this[SET_STORE_ERROR_STATE](false);
      const httpSource = $http.CancelToken.source();
      const timer = setTimeout(() => {
        this[SET_QUICK_VIEW_PRODUCT_DETAILS]({});
        this[SET_QUICK_VIEW_PRODUCT_DETAILS_META]({});
        this[SET_STORE_ERROR_STATE](true);
        httpSource.cancel();
      }, envPropsStore.envProps?.apiTimeout || 0);
      try {
        const res = await $http.get(
          `/xapi/digital/v1/product/${params.productId}?_shoppingMode=${params.shoppingMode}&_regionCode=${params.regionCode}&currencyCode=${params.currencyCode}&_customerState=${params.customerState}&_deviceType=${params.deviceType}`,
          { cancelToken: httpSource.token },
        );
        clearTimeout(timer);
        const productData = res.data;
        if (productData && Object.keys(productData).length > 0) {
          this[SET_QUICK_VIEW_PRODUCT_DETAILS](productData.product[0]);
          this[SET_PRODUCT_DETAILS_API_ERROR_RESPONSE](null);
        } else {
          throw new Error(productData);
        }
        this[SET_QUICK_VIEW_PRODUCT_DETAILS_META](productData);
      } catch (error) {
        clearTimeout(timer);
        this[SET_QUICK_VIEW_PRODUCT_DETAILS_META]({});
        this[SET_QUICK_VIEW_PRODUCT_DETAILS](null);
        this[SET_PRODUCT_DETAILS_API_ERROR_RESPONSE](error);
        this[SET_STORE_ERROR_STATE](true);
      }
    },
    async [UPDATE_PREFERENCE](payload) {
      try {
        const login365FingerPrint = await generateLogin365FingerPrint();
        const response = await $http.put(`${UPDATE_PREFERENCE_ENDPOINT}`, payload, {
          headers: httpUpdateAPIHeaders(payload, login365FingerPrint),
        });
        const isError = response?.data?.errors || response?.data?.error;
        if (isError) {
          this[UPDATE_PREFERENCE_STATUS](false);
          this[SET_REGISTRANT_ITEM_API_RESPONSE](isError?.[0]);
          this[SET_QUICK_VIEW_SAVE_ERROR_STATE](true);
        } else {
          this[UPDATE_PREFERENCE_STATUS](true);
          this[SET_REGISTRANT_ITEM_API_RESPONSE](response);
          this[SET_QUICK_VIEW_SAVE_ERROR_STATE](false);
        }
      } catch (error) {
        SoftSignInValidation(error);
        this[UPDATE_PREFERENCE_STATUS](false);
        this[SET_REGISTRANT_ITEM_API_RESPONSE](error?.response?.data?.errors?.[0]);
        this[SET_QUICK_VIEW_SAVE_ERROR_STATE](true);
      }
    },
    async [ADD_PURCHASE](payload) {
      try {
        const headerBannerStore = useHeaderBannerStore();

        const login365FingerPrint = await generateLogin365FingerPrint();
        /* TODO: work on rootState (now it's {}) parameter (identify which store to get it from) */
        const response = await $http.post(`${ADD_PURCHASE_ENDPOINT}`, payload, {
          headers: httpUpdateAPIHeaders(payload, login365FingerPrint),
        });
        const isError = response?.data?.errors || response?.data?.error;
        if (isError) {
          this[UPDATE_PREFERENCE_STATUS](false);
          this[SET_REGISTRANT_ITEM_API_RESPONSE](isError?.[0]);
          this[SET_QUICK_VIEW_SAVE_ERROR_STATE](true);
        } else {
          this[UPDATE_PREFERENCE_STATUS](true);
          this[SET_REGISTRANT_ITEM_API_RESPONSE](response);
          if (response.data.registryInfo?.result?.[0]?.quantity > 0) {
            headerBannerStore[UPDATE_GIFTS_PURCHASED](response.data.registryInfo?.result?.[0]?.quantity);
          }
        }
      } catch (error) {
        SoftSignInValidation(error);
        this[UPDATE_PREFERENCE_STATUS](false);
        this[SET_REGISTRANT_ITEM_API_RESPONSE](error?.response?.data?.errors?.[0]);
        this[SET_QUICK_VIEW_SAVE_ERROR_STATE](true);
      }
    },
  },
});
